import { $$$ } from '@suntex.core/text';
import { withExtraction, withMatching } from '@suntex.core/text/extensions';

/**
 * Top-level module route segments (e.g. `gyms`, `ss`, `ks`), mirroring the
 * `modules/*` keys in the hub's icon registry (`@suntex.fim/hub`'s
 * `assets/index.ts`). Used to identify legacy short-form hash paths that omit
 * the `/modules` prefix.
 */
const MODULE_TOP_LEVEL_SEGMENTS = new Set([
  'bonus',
  'ct',
  'gyms',
  'jtf',
  'k2',
  'ks',
  'mw',
  'ss',
  'vif',
]);

// Matches legacy short links like `/ss/3` — a module segment followed by a bare number.
const LEGACY_NUMBERED_PATH = /^\/([a-z0-9-]+)\/(\d+)$/i;

/**
 * Normalizes a hub hash path, expanding legacy short-form module links to
 * their canonical `/modules/...` form.
 *
 * @example
 * normalizeHubPath('/ss/3');          // '/modules/ss/ss-3'
 * normalizeHubPath('/gyms/int/add');  // '/modules/gyms/int/add'
 * normalizeHubPath('/stages-of-learning/k'); // unchanged (not a module path)
 */
export function normalizeHubPath(path: string): string {
  if (!path) return path;

  const text = $$$(path, [withMatching(), withExtraction()]);
  if (text.matches(/^\/modules(\/|$)/)) return path;

  const topSegment = text.extractSubstring(/^\/([a-z0-9-]+)/i);
  if (!MODULE_TOP_LEVEL_SEGMENTS.has(topSegment)) return path;

  const [segment, numeral] = text.extractGroups(LEGACY_NUMBERED_PATH);
  if (segment && numeral) {
    return `/modules/${segment}/${segment}-${numeral}`;
  }

  return `/modules${path}`;
}
